import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  Briefcase, 
  IndianRupee, 
  Flower2, 
  GraduationCap, 
  Sparkles,
  Grid, 
  MoreHorizontal, 
  ShieldCheck, 
  Star, 
  Users, 
  Phone, 
  ArrowRight, 
  MessageSquare, 
  Clock, 
  Lock, 
  
  ChevronRight,
  Award
} from 'lucide-react';

// 🟢 Image Asset Imports
import heroZodiacImg from '../../../assets/astrologyDedicatedPageImage/astrology-UI image.webp';
import whyChooseAstroImg from '../../../assets/astrologyDedicatedPageImage/astrology-UI-image-how-it-work.webp';

export const AstrologyMainPage: React.FC = () => {

  // 🟢 "What are you looking for?" Grid Items (Aligned with Database Slugs)
  const categoryCards = [
    { label: 'Love & Relationship', icon: Heart, slug: 'love-and-relationship-astrology' },
    { label: 'Vedic Astrologers', icon: Grid, slug: 'vedic-astrology' },
    { label: 'Career & Business', icon: Briefcase, slug: 'career-and-business-astrology' },
    { label: 'Tarot Card Readers', icon: Lock, slug: 'tarot-card-readers' },
    { label: 'Numerologists', icon: IndianRupee, slug: 'numerology' },
    { label: 'Lal Kitab Experts', icon: Sparkles, slug: 'lal-kitab' },
    { label: 'Health Astrology', icon: Flower2, slug: 'health-astrology' },
    { label: 'Vastu Consultants', icon: GraduationCap, slug: 'vastu-consultants' },
    { label: 'More Services', icon: MoreHorizontal, redirectUrl: '/categories' },
  ];

  // 🟢 "Why Choose trueAdviso" Feature List
  const whyChooseFeatures = [
    {
      title: 'Verified & Experienced Astrologers',
      desc: 'Consult only with trusted and experienced experts.',
      icon: Award
    },
    {
      title: 'Affordable & Transparent Pricing',
      desc: 'Per-minute pricing with no hidden charges.',
      icon: IndianRupee
    },
    {
      title: 'Multiple Consultation Modes',
      desc: 'Connect via Chat, Call or Video as per your convenience.',
      icon: MessageSquare
    },
    {
      title: 'Instant Connect or Schedule Later',
      desc: 'Choose to connect now or book a convenient time slot.',
      icon: Clock
    },
    {
      title: '100% Private & Secure',
      desc: 'Your conversations are encrypted and completely confidential.',
      icon: ShieldCheck
    }
  ];

  // 🟢 Stage of Life Cards (Corrected Mismatched Slugs)
  const stageCards = [
    {
      title: 'Love & Relationships',
      desc: 'Understand compatibility, resolve conflicts and build stronger bonds.',
      slug: 'love-and-relationship-astrology',
      bgGradient: 'from-rose-50 via-pink-50/60 to-rose-100/50',
      border: 'border-rose-200/60',
      btnText: 'text-rose-600',
      emoji: '💖'
    },
    {
      title: 'Career & Business',
      desc: 'Get insights for career growth, job success and business decisions.',
      slug: 'career-and-business-astrology',
      bgGradient: 'from-blue-50 via-indigo-50/60 to-blue-100/50',
      border: 'border-blue-200/60',
      btnText: 'text-blue-600',
      emoji: '💼'
    },
    {
      title: 'Remedies & Vastu Consultants',
      desc: 'Energy balancing solutions through vastu & astrology remedies for home & office.',
      slug: 'vastu-consultants', // 🟢 Fixed: Was career-and-business-astrology
      bgGradient: 'from-emerald-50 via-teal-50/60 to-emerald-100/50',
      border: 'border-emerald-200/60',
      btnText: 'text-emerald-600',
      emoji: '🛕'
    },
    {
      title: 'Health & Wellbeing',
      desc: 'Find solutions for better health, mental peace and happiness.',
      slug: 'health-astrology',
      bgGradient: 'from-purple-50 via-fuchsia-50/60 to-purple-100/50',
      border: 'border-purple-200/60',
      btnText: 'text-purple-600',
      emoji: '🧘‍♀️'
    }
  ];

  // 🟢 Blog Articles
  const blogArticles = [
    {
      tag: 'LOVE & RELATIONSHIP',
      tagBg: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
      title: 'How Astrology Can Improve Your Relationships?',
      desc: 'Learn how planetary alignment and compatibility analysis can bring harmony to your love life.'
    },
    {
      tag: 'MARRIAGE TIMING',
      tagBg: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      title: 'How to Find the Right Time for Marriage?',
      desc: 'Discover how kundli matching and dosha periods help in choosing the perfect time for marriage.'
    },
    {
      tag: 'RELATIONSHIP TIPS',
      tagBg: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
      title: 'Signs Your Relationship is Meant to Be',
      desc: 'Astrological signs that indicate a strong and successful long-term relationship.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800">

      {/* ======================================================================== */}
      {/* 🔮 1. HERO SECTION WITH CLEAR COVER BACKGROUND IMAGE                      */}
      {/* ======================================================================== */}
      <section className="relative overflow-hidden bg-slate-950 text-white pt-6 pb-16">
        
        {/* 🟢 Clean Background Image Layer */}
        <div className="absolute inset-0 pointer-events-none z-0">
          <img 
            src={heroZodiacImg} 
            alt="Astrology Celestial Background" 
            className="w-full h-full object-cover object-center"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          
          {/* Breadcrumbs */}
          <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-8">
            <Link to="/" className="hover:text-amber-400 transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-slate-200 font-medium">Astrology</span>
          </div>

          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">
              <span className="text-amber-400">Astrology</span> for Every Question in Life
            </h1>

            <p className="text-base sm:text-lg text-slate-300 max-w-xl font-medium leading-relaxed">
              Get clarity on love, relationships, marriage, career and more with guidance from verified astrologers.
            </p>

            {/* 3 Top Trust Badges */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-2.5 backdrop-blur-md">
                <Lock className="w-4 h-4 text-amber-400 shrink-0" />
                <div>
                  <h4 className="text-xs font-bold text-white">100% Private</h4>
                  <p className="text-[10px] text-slate-400">1-to-1 Consultations</p>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-2.5 backdrop-blur-md">
                <Star className="w-4 h-4 text-amber-400 shrink-0 fill-amber-400" />
                <div>
                  <h4 className="text-xs font-bold text-white">Verified Astrologers</h4>
                  <p className="text-[10px] text-slate-400">Trusted & Experienced</p>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-3 flex items-center gap-2.5 backdrop-blur-md">
                <ShieldCheck className="w-4 h-4 text-amber-400 shrink-0" />
                <div>
                  <h4 className="text-xs font-bold text-white">Secure & Safe</h4>
                  <p className="text-[10px] text-slate-400">Encrypted Conversations</p>
                </div>
              </div>
            </div>

            {/* CTA Action Buttons */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
<Link 
  to="/experts/astrology"
  className="px-8 py-4 bg-amber-400 hover:bg-amber-500 text-slate-950 font-extrabold rounded-full shadow-lg shadow-amber-400/20 transition-all flex items-center gap-2 text-sm sm:text-base cursor-pointer"
>
  <Phone className="w-4 h-4 fill-current" />
  <span>Chat / Call an Astrologer Now</span>
  <ArrowRight className="w-4 h-4 ml-1" />
</Link>

              <Link 
                to="/categories"
                className="px-6 py-4 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold rounded-full transition-all flex items-center gap-2 text-sm sm:text-base backdrop-blur-md cursor-pointer"
              >
                <span>Explore Services</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Bottom Privacy Ribbon */}
            <div className="flex items-center gap-2 text-xs text-slate-300 font-semibold pt-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Your Privacy Comes First – 1-to-1 Consultations, No Data Sharing, Encrypted Chats</span>
            </div>

          </div>

        </div>
      </section>


      {/* ======================================================================== */}
      {/* 🔮 2. "WHAT ARE YOU LOOKING FOR?" GRID SECTION                          */}
      {/* ======================================================================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center space-y-2 mb-8">
          <div className="flex items-center justify-center gap-2 text-amber-500 font-extrabold text-sm uppercase tracking-widest">
            <span>✨</span>
            <span>What are you looking for?</span>
            <span>✨</span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
          {categoryCards.map((item, idx) => {
            const IconComp = item.icon;
            const targetPath = item.redirectUrl || `/experts/astrology/${item.slug}`;

            return (
              <Link
                key={idx}
                to={targetPath}
                className="bg-white hover:bg-indigo-50/50 border border-slate-100 hover:border-indigo-200 rounded-2xl p-4 flex flex-col items-center justify-center text-center gap-3 transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer group"
              >
                <div className="w-12 h-12 rounded-full bg-indigo-50 group-hover:bg-amber-100 text-indigo-600 group-hover:text-amber-700 flex items-center justify-center transition-colors">
                  <IconComp className="w-6 h-6" />
                </div>
                <span className="text-xs font-bold text-slate-800 leading-tight group-hover:text-indigo-900">
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </section>


      {/* ======================================================================== */}
      {/* 🔮 3. "WHY CHOOSE TRUEADVISO ASTROLOGY?" SECTION                        */}
      {/* ======================================================================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-indigo-50/40 border border-indigo-100 rounded-3xl p-6 sm:p-10">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 text-center mb-8">
            Why Choose <span className="text-rose-600">true</span><span className="text-amber-500">Adviso</span> Astrology?
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            
            {/* Left Image Card */}
            <div className="lg:col-span-5 bg-white rounded-2xl p-4 border border-slate-100 shadow-sm flex items-center justify-center overflow-hidden">
              <img 
                src={whyChooseAstroImg} 
                alt="Vedic & Kundli Experts" 
                className="w-full max-w-sm h-64 sm:h-72 object-cover rounded-xl"
              />
            </div>

            {/* Right Feature List */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {whyChooseFeatures.map((feat, idx) => {
                const IconComp = feat.icon;
                return (
                  <div key={idx} className="flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center shrink-0 shadow-md">
                      <IconComp className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm">{feat.title}</h4>
                      <p className="text-xs text-slate-600 leading-relaxed mt-0.5">{feat.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </section>


      {/* ======================================================================== */}
      {/* 🔮 4. "FIND THE RIGHT GUIDANCE FOR EVERY STAGE OF LIFE"                 */}
      {/* ======================================================================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-6">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 text-center">
          Find the Right Guidance for Every Stage of Life
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stageCards.map((card, idx) => (
            <div 
              key={idx}
              className={`bg-gradient-to-b ${card.bgGradient} border ${card.border} rounded-2xl p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow`}
            >
              <div className="space-y-3">
                <span className="text-3xl">{card.emoji}</span>
                <h3 className="font-extrabold text-slate-900 text-lg">{card.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{card.desc}</p>
              </div>

              <Link 
                to={`/experts/astrology/${card.slug}`}
                className={`pt-6 flex items-center gap-1.5 font-bold text-xs ${card.btnText} hover:underline cursor-pointer`}
              >
                <span>Explore Now</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>
      </section>


      {/* ======================================================================== */}
      {/* 🔮 5. "ASTROLOGY INSIGHTS & GUIDANCE" (DARK BLOG CAROUSEL CONTAINER)     */}
      {/* ======================================================================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-slate-950 text-white rounded-3xl p-6 sm:p-10 shadow-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            
            {/* Left Column Header */}
            <div className="lg:col-span-3 flex flex-col justify-between space-y-4">
              <div>
                <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight">
                  Astrology Insights & Guidance
                </h2>
                <div className="w-12 h-1 bg-amber-400 rounded-full my-3" />
                <p className="text-xs text-slate-400 leading-relaxed font-medium">
                  Helpful articles to bring clarity to your life and relationships.
                </p>
              </div>

              <Link 
                to="/blogs"
                className="px-5 py-2.5 border border-white/20 hover:border-amber-400 text-white hover:text-amber-400 rounded-xl font-bold text-xs transition-colors self-start flex items-center gap-2 cursor-pointer"
              >
                <span>View All Articles</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            {/* Right Cards List */}
            <div className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {blogArticles.map((article, idx) => (
                <div key={idx} className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col justify-between hover:border-slate-700 transition-colors">
                  <div className="space-y-3">
                    <span className={`inline-block px-2.5 py-1 rounded-md text-[10px] font-extrabold border ${article.tagBg}`}>
                      {article.tag}
                    </span>
                    <h3 className="font-bold text-white text-sm leading-snug">{article.title}</h3>
                    <p className="text-xs text-slate-400 leading-relaxed">{article.desc}</p>
                  </div>

                  <Link 
                    to="/blogs"
                    className="pt-4 text-amber-400 font-bold text-xs flex items-center gap-1 hover:underline cursor-pointer"
                  >
                    <span>Read More</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>


      {/* ======================================================================== */}
      {/* 🔮 6. STATS HIGHLIGHTS BAR                                               */}
      {/* ======================================================================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          
          <div className="flex flex-col items-center">
            <Users className="w-6 h-6 text-indigo-600 mb-2" />
            <span className="text-2xl font-extrabold text-slate-900">50K+</span>
            <span className="text-xs text-slate-500 font-medium">Happy Customers</span>
          </div>

          <div className="flex flex-col items-center">
            <Star className="w-6 h-6 text-amber-500 fill-amber-500 mb-2" />
            <span className="text-2xl font-extrabold text-slate-900">4.8/5</span>
            <span className="text-xs text-slate-500 font-medium">Average Rating</span>
          </div>

          <div className="flex flex-col items-center">
            <Award className="w-6 h-6 text-purple-600 mb-2" />
            <span className="text-2xl font-extrabold text-slate-900">200+</span>
            <span className="text-xs text-slate-500 font-medium">Verified Astrologers</span>
          </div>

          <div className="flex flex-col items-center">
            <MessageSquare className="w-6 h-6 text-rose-500 mb-2" />
            <span className="text-2xl font-extrabold text-slate-900">1M+</span>
            <span className="text-xs text-slate-500 font-medium">Consultations Done</span>
          </div>

        </div>
      </section>


      {/* ======================================================================== */}
      {/* 🔮 7. PROMO WELCOME BANNER                                               */}
      {/* ======================================================================== */}
      {/* <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-950 rounded-3xl p-6 sm:p-8 text-white shadow-xl flex flex-col sm:flex-row items-center justify-between gap-6">
          
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-amber-400 text-slate-950 flex items-center justify-center shrink-0 shadow-lg">
              <Gift className="w-8 h-8" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-extrabold">New to trueAdviso Astrology?</h3>
              <p className="text-xs sm:text-sm text-slate-300 mt-1">
                Get <span className="text-amber-300 font-bold">₹50 welcome credit</span> in your wallet to connect with astrologers.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 shrink-0">
            <div className="border border-dashed border-amber-400/60 bg-amber-400/10 px-4 py-2 rounded-xl text-center">
              <span className="block text-xs font-bold text-amber-300 tracking-wider uppercase">WELCOME50</span>
              <span className="block text-[10px] text-slate-300">Use Code</span>
            </div>

            <Link 
              to="/user-signup"
              className="px-6 py-3 bg-amber-400 hover:bg-amber-500 text-slate-950 font-extrabold rounded-full text-xs sm:text-sm transition-all shadow-md cursor-pointer"
            >
              Claim Offer →
            </Link>
          </div>

        </div>
      </section> */}


      {/* ======================================================================== */}
      {/* 🔮 8. PRIVACY BANNER & FOOTER DISCLAIMER                                */}
      {/* ======================================================================== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-12 space-y-4">
        
        {/* Privacy Card */}
        <div className="bg-indigo-50/60 border border-indigo-100 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-indigo-600 shrink-0" />
            <div>
              <h4 className="font-bold text-slate-900 text-xs sm:text-sm">Your Privacy Comes First</h4>
              <p className="text-[11px] text-slate-600">1-to-1 Consultations • No Data Sharing • Encrypted Chats</p>
            </div>
          </div>

          <Link 
            to="/about"
            className="px-4 py-2 bg-white border border-indigo-200 text-indigo-700 font-bold rounded-xl text-xs hover:bg-indigo-100 transition-colors cursor-pointer shrink-0"
          >
            Know More →
          </Link>
        </div>

      </section>

    </div>
  );
};

export default AstrologyMainPage;